import React, { ChangeEvent, HTMLInputTypeAttribute, FC, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import selectChevron from '@/assets/icons/select-chevron.svg';
import { ISRAELI_LANDLINE_PREFIXES, ISRAELI_MOBILE_PREFIXES, isFullIsraeliMobileDigits, parseIsraeliPhone } from '@/helpers/phoneHelper';
import styles from './components.module.css';

interface LabeledInputProps {
  iconSrc?: string;
  disabledIconSrc?: string;
  iconAlt?: string;
  label: string;
  id?: string;
  type?: HTMLInputTypeAttribute;
  placeholder?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  width?: string;
  required?: boolean;
  mobileFullWidth?: boolean;
  /** Passed straight to the native input - used to bound date fields (SPR-1179). */
  min?: string | number;
  max?: string | number;
  /** Passed straight to the native input - digit-only fields ask for the numeric keypad. */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  name?: string;
  fieldHint?: string;
  fieldHintId?: string;
  /** When false, hint text is not rendered here (parent renders it, e.g. outside a flex row). */
  showFieldHint?: boolean;
  /** When set, overrides aria-invalid derived from fieldHint (e.g. too-long paste with valid 9 digits). */
  fieldAriaInvalid?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const LabeledInput: FC<LabeledInputProps> = ({
  iconSrc,
  disabledIconSrc,
  iconAlt,
  label,
  id,
  type = 'text',
  placeholder,
  value,
  onChange,
  onKeyDown,
  disabled,
  width = '',
  required = true,
  mobileFullWidth = false,
  min,
  max,
  inputMode,
  autoComplete,
  name,
  fieldHint,
  fieldHintId,
  showFieldHint = true,
  fieldAriaInvalid,
  onBlur,
}) => {
  const { t } = useTranslation();
  const displayIconSrc = disabled ? disabledIconSrc || iconSrc : iconSrc;
  const hasIcon = !!(iconSrc || disabledIconSrc);
  const inputAriaInvalid =
    fieldAriaInvalid !== undefined ? (fieldAriaInvalid ? true : undefined) : fieldHint ? true : undefined;

  return (
    <div className={mobileFullWidth ? styles.mobileFullWidth : ''} style={{ width }}>
      <label htmlFor={id} className={`body-sm-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-heading)'} block mb-1.5`}>
        {t(label)} {!required && <span className="text-(--font-body-3)">({t('optional')})</span>}
      </label>
      <div className={styles.inputContainer}>
        <input
          id={id}
          className={`${styles.input} body-md-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-body-1)'}`}
          style={{ paddingInlineStart: hasIcon ? '47px' : '12px' }}
          type={type}
          placeholder={t(placeholder || '')}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          inputMode={inputMode}
          autoComplete={autoComplete}
          name={name}
          onBlur={onBlur}
          aria-invalid={inputAriaInvalid}
          aria-describedby={fieldHint && fieldHintId ? fieldHintId : undefined}
        />
        {hasIcon && <img className={`${styles.icon}`} src={displayIconSrc!} alt={iconAlt || 'Input icon'} />}
      </div>
      {showFieldHint && fieldHintId && (
        <p id={fieldHintId} className={`body-sm-regular mt-1 ${styles.fieldHintStatus}`} role="status" aria-live="polite">
          {fieldHint ?? ''}
        </p>
      )}
    </div>
  );
};

export default LabeledInput;

// --- PhoneInput value type ---
interface PhoneInputProps {
  label: string;
  id?: string;
  value?: string; // full phone number, e.g. '0521234567'
  onChange?: (val: string) => void;
  disabled?: boolean;
  width?: string;
  required?: boolean;
  isMobile?: boolean;
  mobileFullWidth?: boolean;
  autoComplete?: string;
  name?: string;
  /** When set, overrides default `tel` on the subscriber number field (SPR-1241 autofill). */
  numberInputType?: HTMLInputTypeAttribute;
}

export const PhoneInput: FC<PhoneInputProps> = ({
  label,
  id,
  value = '',
  onChange,
  disabled,
  width = '',
  required = false,
  isMobile = false,
  mobileFullWidth = false,
  autoComplete,
  name,
  numberInputType = 'tel',
}) => {
  const { t } = useTranslation();
  const prefixes = isMobile ? ISRAELI_MOBILE_PREFIXES : ISRAELI_LANDLINE_PREFIXES;
  const parsedValue = parseIsraeliPhone(value, prefixes);

  const [prefix, setPrefix] = useState<string>(parsedValue.prefix);
  const [number, setNumber] = useState<string>(parsedValue.number);
  const [prefixDropdownOpen, setPrefixDropdownOpen] = useState(false);
  const [subscriberHint, setSubscriberHint] = useState('');
  const prefixRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parsed = parseIsraeliPhone(value, prefixes);
    setPrefix(parsed.prefix);
    setNumber(parsed.number);
  }, [value, prefixes]);

  useEffect(() => {
    if (!prefixDropdownOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (prefixRef.current && !prefixRef.current.contains(event.target as Node)) {
        setPrefixDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [prefixDropdownOpen]);

  const emitChange = (nextPrefix: string, nextNumber: string) => {
    setPrefix(nextPrefix);
    setNumber(nextNumber);
    onChange?.(nextPrefix + nextNumber);
  };

  const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '');
    const looksLikeFullNumber =
      raw.trim().startsWith('+') ||
      (digits.startsWith('0') && digits.length >= 9) ||
      (digits.startsWith('972') && digits.length >= 11) ||
      (isMobile && isFullIsraeliMobileDigits(digits));
    const toParse = looksLikeFullNumber || !prefix ? raw : prefix + digits;
    const parsed = parseIsraeliPhone(toParse, prefixes);
    if (parsed.subscriberOverflow) {
      setSubscriberHint(t('phoneSubscriberDigitsAfterPrefix'));
    } else {
      setSubscriberHint('');
    }
    emitChange(parsed.prefix, parsed.number);
  };

  return (
    <div className={mobileFullWidth ? styles.mobileFullWidth : ''} style={{ width }} data-testid={id ? `phone-input-${id}` : 'phone-input'}>
      <label htmlFor={id} className={`body-sm-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-heading)'} block mb-1.5`}>
        {label} {!required && <span className="text-(--font-body-3)">({t('optional')})</span>}
      </label>
      <div className={`${styles.inputContainer} ${styles.PhoneInputContainer} body-md-regular`}>
        <div className="relative" ref={prefixRef}>
          <button
            type="button"
            className={`${styles.prefixSelect} ${prefixDropdownOpen ? styles.open : ''}`}
            onClick={() => setPrefixDropdownOpen((open) => !open)}
            disabled={disabled}
            tabIndex={0}
            aria-haspopup="listbox"
            aria-expanded={prefixDropdownOpen}
            aria-label={t('phoneNumber')}
          >
            {prefix && !disabled ? (
              <span
                className={styles.prefixClear}
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPrefixDropdownOpen(false);
                  emitChange('', '');
                }}
                aria-label="Clear prefix"
              >
                ×
              </span>
            ) : (
              <img src={selectChevron} alt="" className={`${styles.prefixChevron} ${prefixDropdownOpen ? styles.open : ''}`} />
            )}
            <span>{prefix || (isMobile ? '\xa0\xa0\xa0\xa0\xa0\xa0\xa0' : '\xa0\xa0\xa0\xa0\xa0')}</span>
          </button>
          {prefixDropdownOpen && (
            <div className={`${styles.prefixDropdown} ${styles.dropUp}`} role="listbox">
              {prefixes.map((p) => (
                <button
                  key={p}
                  className={`${styles.prefixOption} body-md-regular ${prefix === p ? styles.active : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setPrefixDropdownOpen(false);
                    emitChange(p, number.slice(0, 7));
                  }}
                  type="button"
                  role="option"
                  aria-selected={prefix === p}
                  tabIndex={0}
                  disabled={disabled}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          id={id}
          className={`${styles.input} body-md-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-body-1)'}`}
          type={numberInputType}
          placeholder={'XXXXXXX'}
          value={number}
          onChange={handleNumberChange}
          disabled={disabled}
          required={required}
          maxLength={16}
          inputMode="numeric"
          autoComplete={autoComplete}
          name={name}
          aria-describedby={subscriberHint && id ? `${id}-subscriber-hint` : undefined}
        />
      </div>
      {id && (
        <p id={`${id}-subscriber-hint`} className={`body-sm-regular mt-1 ${styles.fieldHintStatus}`} role="status" aria-live="polite">
          {subscriberHint}
        </p>
      )}
    </div>
  );
};

interface SearchableSelectProps {
  label: string;
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  width?: string;
  required?: boolean;
  loadOptions: (dependency?: string) => Promise<string[]>;
  dependency?: string;
  placeholder?: string;
  dependencyPlaceholder?: string;
  maxResults?: number;
  mobileFullWidth?: boolean;
}

export const SearchableSelect: FC<SearchableSelectProps> = ({
  label,
  id,
  value,
  onChange,
  disabled,
  width = '',
  required = true,
  loadOptions,
  dependency,
  placeholder = '',
  dependencyPlaceholder = '',
  maxResults,
  mobileFullWidth = false,
}) => {
  const { t } = useTranslation();
  const isControlled = value !== undefined;
  const valueRef = useRef(value);
  valueRef.current = value;
  const [searchTerm, setSearchTerm] = useState(() => (isControlled ? value : ''));
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const isFocusedRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const syncSearchTermFromValue = useCallback((externalValue: string) => {
    const idPart = externalValue ? externalValue.split(' - ')[0] : '';
    if (!idPart) {
      setSearchTerm('');
      return;
    }
    const matchingOption = optionsRef.current.find((opt) => opt.split(' - ')[0] === idPart);
    setSearchTerm(matchingOption || idPart);
  }, []);

  // Sync searchTerm with `value` when it changes externally (including external clears).
  // Skip while the user is typing/focused — otherwise each onChange echo replaces partial
  // input with the full matched option (SPR-1240 branch field in new support request).
  useEffect(() => {
    if (!isControlled || isFocusedRef.current) return;
    syncSearchTermFromValue(value);
  }, [value, isControlled, syncSearchTermFromValue]);

  // Load data when dependency changes
  useEffect(() => {
    const loadData = async () => {
      // If we have a dependency requirement but no dependency value, clear options
      if (dependencyPlaceholder && !dependency) {
        setOptions([]);
        setFilteredOptions([]);
        return;
      }

      setLoading(true);
      try {
        const data = await loadOptions(dependency);
        setOptions(data);
        setFilteredOptions(data);
      } catch (error) {
        console.error('Failed to load options:', error);
        setOptions([]);
        setFilteredOptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dependency, dependencyPlaceholder, loadOptions]);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (onChange) {
      onChange(newValue);
    }
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option: string) => {
    // Show the FULL option (e.g. "12 - בנק הפועלים") so the user gets a clear visual
    // confirmation of what they picked, not just the id. Keeping searchTerm as the
    // full string also matches what the [value, options] sync effect will compute on
    // the next render, so we don't flicker between full label and id.
    setSearchTerm(option);
    if (onChange) {
      onChange(option);
    }
    setIsOpen(false);
  };

  const canOpenDropdown = () => !dependencyPlaceholder || Boolean(dependency);

  const handleInputFocus = () => {
    isFocusedRef.current = true;
    if (canOpenDropdown()) {
      setIsOpen(true);
    }
  };

  const handleInputClick = () => {
    if (canOpenDropdown()) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow option selection
    setTimeout(() => {
      setIsOpen(false);
      isFocusedRef.current = false;
      if (isControlled) {
        syncSearchTermFromValue(valueRef.current ?? '');
      }
    }, 200);
  };

  const getPlaceholder = () => {
    if (dependencyPlaceholder && !dependency) {
      return t(dependencyPlaceholder);
    }
    if (loading) {
      return t('loading') + '...';
    }
    return t(placeholder);
  };

  const isInputDisabled = disabled || loading || (dependencyPlaceholder && !dependency);
  const shouldShowDropdown = isOpen && filteredOptions.length > 0 && (!dependencyPlaceholder || dependency);

  const displayOptions = maxResults ? filteredOptions.slice(0, maxResults) : filteredOptions;

  // Prefer the full searchTerm (which is the full "id - label" after a selection,
  // or whatever the user is currently typing) over the bare id from `value`. This
  // gives the user a clear visual confirmation of the selected option instead of
  // showing only the id digit.
  const displayValue = searchTerm || (isControlled && value ? value.split(' - ')[0] : '');

  return (
    <div className={mobileFullWidth ? styles.mobileFullWidth : ''} style={{ width }}>
      <label htmlFor={id} className={`body-sm-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-heading)'} block mb-1.5`}>
        {t(label)} {!required && <span className="text-(--font-body-3)">({t('optional')})</span>}
      </label>
      <div className={`${styles.inputContainer} relative`}>
        <img
          src={selectChevron}
          alt=""
          className={`${styles.prefixChevron} ${isOpen ? styles.open : ''}`}
          style={{
            position: 'absolute',
            insetInlineEnd: '12px',
            top: '50%',
            transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}`,
            transition: 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)',
            pointerEvents: 'none',
          }}
        />
        <input
          id={id}
          className={`${styles.input} body-md-regular ${disabled ? 'text-(--font-disabled)' : 'text-(--font-body-1)'}`}
          style={{ paddingInlineEnd: '36px' }}
          type="text"
          placeholder={getPlaceholder()}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          disabled={!!isInputDisabled}
          required={required || false}
          autoComplete="off"
        />
        {shouldShowDropdown && (
          <div className={styles.prefixDropdown} style={{ minWidth: width || '200px', maxHeight: '200px', overflowY: 'auto' }}>
            {displayOptions.map((option) => {
              const idPart = option.split(' - ')[0];
              const displayIdPart = displayValue ? displayValue.split(' - ')[0] : '';
              const isActive = displayIdPart === idPart;
              return (
                <button
                  key={option}
                  className={`${styles.selectOption} body-md-regular ${isActive ? styles.active : ''}`}
                  // Prevent the input from blurring when the user presses on an option.
                  // Without this the option's mousedown triggers handleInputBlur, which
                  // schedules setIsOpen(false) on a timeout - and on some machines the
                  // dropdown can unmount before the click event reaches this button,
                  // making selections silently fail. Using onMouseDown to handle the
                  // selection makes it fire as soon as the user presses on the option,
                  // independent of the focus/blur timing.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleOptionSelect(option);
                  }}
                  type="button"
                  tabIndex={0}
                >
                  {option}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
